/**
 * Os sons da UO Classic que o jogo usa (docs/proposta-sons.md). Cada número é o id do som no
 * `soundLegacyMUL.uop`; o arquivo fica em `/sounds/uo/<id em hex, 4 dígitos>.mp3`, gerado por
 * `tools/uo-sons.py` a partir desta tabela (o script lê todos os `0x…` deste arquivo).
 *
 * A regra do golpe é a da UO: quem bate toca o som da arma (ou o ataque da espécie) e quem
 * apanha toca a própria dor (a da espécie, o "oomph" do gênero ou a madeira do manequim).
 */

/** Os 5 sons seguidos a partir do BaseSoundID do RunUO. */
export const RAIVA = 0, OCIOSO = 1, ATAQUE = 2, DOR = 3, MORTE = 4;
const base = (b: number): readonly number[] => [b, b + 1, b + 2, b + 3, b + 4];

/** Mob → os 5 sons, pela aparência (a pasta do sprite em `sprites/enemies/`). */
export const MOB_SONS: Readonly<Record<string, readonly number[]>> = {
  skeleton: base(0x48D), zombie: base(0x1D7), 'spirit-ghost': base(0x482), lich: base(0x3E9),
  orc: base(0x45A), 'orc-captain': base(0x45A), 'orcish-lord': base(0x45A), 'orcish-mage': base(0x45A),
  'arctic-ogre-lord': base(0x1AB), 'frost-troll': base(0x1CD), 'frost-ooze': base(0x1C8), 'frost-spider': base(0x388),
  'ice-elemental': base(0x10C), 'snow-elemental': base(0x107), 'ice-fiend': base(0x165), 'balron-2118': base(0x165), 'balron-1258': base(0x165), 'balron-1266': base(0x165), 'balron-1151': base(0x165), 'balron-1157': base(0x165), 'ice-serpent': base(0xDB),
  'ice-snake': base(0xDB), 'white-wyrm': base(0x16A), 'fire-elemental': base(0x346), 'fire-steed': base(0xA8),
  efreet: base(0x300), 'hell-cat': base(0x69), 'predator-hellcat': base(0xBA), 'hell-hound': base(0xE5),
  'lava-lizard': base(0x5A), 'lava-serpent': base(0xDB), 'lava-snake': base(0xDB), 'daemon-red': base(0x165),
  drake: base(0x16A), wyvern: base(0x16A), dragon: base(0x16A), 'shadow-wyrm': base(0x16A), 'ancient-wyrm': base(0x16A)
};

/** Montaria → os 5 sons, pelo começo do id do bicho (`world/montarias.ts`). */
export const MONTARIA_SONS: readonly (readonly [string, readonly number[]])[] = [
  ['cavalo', base(0xA8)], ['horse', base(0xA8)], ['unicornio', base(0x4BC)], ['ostard', base(0x270)],
  ['besouro', base(0x21D)], ['dragao', base(0x16A)], ['cu-sidhe', [0x578, 0x577, 0x576, 0x579, 0x575]],
  ['hiryu', [0x4FE, 0x4FD, 0x4FC, 0x4FF, 0x4FB]], ['urso', base(0xA3)], ['ridgeback', base(0x3F3)],
  ['llama', base(0x3F3)], ['esqueleto', base(0x1C3)]
];

export interface SomDeArma { readonly acerto: readonly number[]; readonly erro: readonly number[] }

/** Arma → acerto e erro, pelo defId base (sem o `@material`). Os DefHitSound/DefMissSound do RunUO. */
export const ARMA_SONS: Readonly<Record<string, SomDeArma>> = {
  katana: { acerto: [0x23B], erro: [0x23A] },
  dagger: { acerto: [0x23C], erro: [0x238] },
  'large-battle-axe': { acerto: [0x232], erro: [0x23A] },
  'gnarled-staff': { acerto: [0x233], erro: [0x239] },
  bow: { acerto: [0x234], erro: [0x238] },
  'heavy-crossbow': { acerto: [0x234], erro: [0x238] }
};
/** Sem arma (ou arma sem linha acima): o soco. */
export const SOCO: SomDeArma = { acerto: [0x135, 0x136, 0x137, 0x138, 0x139, 0x13A, 0x13B, 0x13C, 0x13D], erro: [0x238] };

export const DOR_F = [0x14B, 0x14C, 0x14D, 0x14E, 0x14F] as const;
export const DOR_M = [0x154, 0x155, 0x156, 0x157, 0x158, 0x159] as const;
export const MORTE_F = [0x150, 0x151, 0x152, 0x153] as const;
export const MORTE_M = [0x15A, 0x15B, 0x15C, 0x15D] as const;
/** O manequim apanhando: a madeira do TrainingDummy do RunUO. */
export const MANEQUIM = [0x3A4, 0x3A6, 0x3A9, 0x3AE, 0x3B4, 0x3B6] as const;

/** Passos (o ritmo do ClassicUO): a pé e montado andando alternam 0x12B/0x12C; montado correndo, 0x129/0x12A. */
export const PASSO = [0x12B, 0x12C] as const;
export const PASSO_GALOPE = [0x129, 0x12A] as const;
export const PASSO_MS = 400, PASSO_MONTADO_MS = 350, PASSO_GALOPE_MS = 150;

export const OFICIO = { forja: 0x2A, alquimia: 0x242, alfaiataria: 0x248, arcos: 0x55, garrafa: 0x240 } as const;
export const COLETA = { minerar: [0x125, 0x126], madeira: [0x13E] } as const;

export const ITEM = {
  pocao: 0x2D6,
  bandagem: 0x57,
  /** Prata pela quantidade: 1, até 5, mais (o Gold do RunUO). */
  prata: [0x2E4, 0x2E5, 0x2E6],
  gema: [0x2E1, 0x2E2, 0x2E3],
  comercio: 0x32,
  equiparArma: 0x56,
  bancoAbre: 0x2D,
  bancoFecha: 0x2C
} as const;

export const MUNDO = { portal: 0x20E, ankh: 0x214 } as const;
/** Portas das casas (techspec-casas §03.14): os sons do WoodenDoor e do MetalDoor do ServUO. */
export const PORTA = { madeiraAbre: 0xEA, madeiraFecha: 0xF1, metalAbre: 0xEC, metalFecha: 0xF3 } as const;
export const EVENTO = { abriu: 0x2E7, aviso: 0x66C, podio: 0x5B5 } as const;
export const AVISO_UI = 0x5B6;

/** Magias cujo som da tabela antiga estava trocado (a proposta, §02). */
export const MAGIA_UO: Readonly<Record<string, number>> = {
  // O bafo dos mobs: BreathEffectSound do ServUO (BaseCreature), o mesmo para fogo e gelo.
  'bafo-fogo': 0x227,
  'bafo-gelo': 0x227,
  'night-sight': 0x1E3,
  'magic-reflection': 0x1E9,
  // Buff e debuff (techspec-magias-de-efeito): os sons das magias no ServUO.
  clumsy: 0x1DF,
  feeblemind: 0x1E4,
  weaken: 0x1E6,
  'reactive-armor': 0x1F2,
  agility: 0x1E7,
  cunning: 0x1EB,
  strength: 0x1EE,
  protection: 0x1ED,
  bless: 0x1EA,
  'arch-protection': 0x299,
  curse: 0x1E1,
  lightning: 0x29,
  'chain-lightning': 0x29,
  'guarda-raio': 0x29,
  explosion: 0x307,
  earthquake: 0x2F3
};

/** Ambiente por mapa: um som sorteado a cada `minS`–`maxS` segundos, no volume `vol` do canal Ambiente. */
export interface Ambiente { readonly sons: readonly number[]; readonly minS: number; readonly maxS: number; readonly vol: number }
const CIDADE: Ambiente = { sons: [0x17, 0x18, 0x19, 0x1A, 0x1B], minS: 10, maxS: 25, vol: 0.35 };
const MASMORRA: Ambiente = { sons: [0x2D7, 0x2D8, 0x2D9, 0x2DB, 0x2DC], minS: 8, maxS: 20, vol: 0.4 };
export const AMBIENTE: Readonly<Record<string, Ambiente>> = {
  minoc: CIDADE, cove: CIDADE, 'minoc-village': CIDADE, britain: CIDADE, 'serpents-hold': CIDADE,
  'floresta-yew': { sons: [0x0, 0x1, 0x2], minS: 6, maxS: 14, vol: 0.4 },
  'cemiterio-britain': { sons: [0x7D, 0x7E, 0x7F, 0x80, 0x81], minS: 9, maxS: 22, vol: 0.4 },
  'cemiterio-cove': { sons: [0x7D, 0x7E, 0x7F, 0x80, 0x81], minS: 9, maxS: 22, vol: 0.4 },
  'mina-minoc': MASMORRA, destard: MASMORRA, 'orc-fort-yew': CIDADE,
  'fire-dungeon': { sons: [0x1DD, 0x345], minS: 5, maxS: 10, vol: 0.35 },
  'ice-dungeon': { sons: [0x14, 0x15, 0x16], minS: 5, maxS: 10, vol: 0.35 }
};

/** Arquivo do som. */
export function urlUo(id: number): string {
  return `/sounds/uo/${id.toString(16).padStart(4, '0')}.mp3`;
}

export function sorteia(ids: readonly number[], evita = -1): number {
  if (ids.length === 0) return -1;
  let i = (Math.random() * ids.length) | 0;
  if (ids.length > 1 && ids[i] === evita) i = (i + 1) % ids.length;
  return ids[i];
}

export function somDaArma(defId: string | null | undefined): SomDeArma {
  const id = (defId ?? '').split('@')[0].trim();
  return ARMA_SONS[id] ?? SOCO;
}

export function sonsDaMontaria(defId: string | null | undefined): readonly number[] | null {
  const id = (defId ?? '').trim();
  for (const [prefixo, sons] of MONTARIA_SONS) if (id === prefixo || id.startsWith(`${prefixo}-`)) return sons;
  return null;
}
